<template>
  <div class="admin-dashboard">
    <h1>Admin Dashboard</h1>

    <div v-if="loading" class="loading">Loading...</div>
    <div v-else>
      <div class="stats-grid">
        <div class="card">
          <h3>Total Users</h3>
          <p class="big-number">{{ stats.userCount }}</p>
        </div>
        <div class="card">
          <h3>Kanji in DB</h3>
          <p class="big-number">{{ stats.kanjiCount }}</p>
        </div>
        <div class="card">
          <h3>System Status</h3>
          <p class="status-ok">{{ stats.systemStatus }}</p>
        </div>
      </div>

      <div class="user-management">
        <h2>User Management</h2>
        <table class="user-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Role</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in users" :key="user._id">
              <td>{{ user.username }}</td>
              <td>
                <span :class="['role-badge', user.role]">{{ user.role }}</span>
              </td>
              <td>{{ new Date(user.createdAt).toLocaleDateString() }}</td>
              <td>
                <button 
                  @click="viewProfile(user._id)" 
                  class="btn-view"
                >View</button>
                <button 
                  v-if="user.role !== 'admin'" 
                  @click="deleteUser(user._id)" 
                  class="btn-delete"
                >Delete</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import { useRouter } from 'vue-router';

const stats = ref({});
const users = ref([]);
const loading = ref(true);
const router = useRouter();

const token = localStorage.getItem('token');
// Ideally check role here, but API will block anyway

onMounted(async () => {
  if (!token) {
    router.push('/login');
    return;
  }
  await fetchData();
});

async function fetchData() {
  try {
    const opts = { headers: { 'x-auth-token': token } };
    
    const [statsRes, usersRes] = await Promise.all([
      axios.get('/api/admin/stats', opts),
      axios.get('/api/admin/users', opts)
    ]);

    stats.value = statsRes.data;
    users.value = usersRes.data;
  } catch (err) {
    console.error(err);
    if (err.response && err.response.status === 403) {
      alert("Access Denied: Admin only");
      router.push('/');
    }
  } finally {
    loading.value = false;
  }
}

async function deleteUser(id) {
  if (!confirm("Are you sure you want to delete this user? This cannot be undone.")) return;
  
  try {
    await axios.delete(`/api/admin/users/${id}`, {
      headers: { 'x-auth-token': token }
    });
    // Remove from local list
    users.value = users.value.filter(u => u._id !== id);
    // Refresh stats
    stats.value.userCount--;
  } catch (e) {
    alert("Failed to delete user");
    console.error(e);
  }
}

function viewProfile(id) {
    router.push(`/profile/${id}`);
}
</script>

<style scoped>
.admin-dashboard {
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem;
  font-family: 'Segoe UI', sans-serif;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.card {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  text-align: center;
}

.big-number {
  font-size: 2.5rem;
  font-weight: bold;
  color: #4f8cff;
  margin: 0.5rem 0;
}

.status-ok {
  color: #16a34a;
  font-weight: bold;
  font-size: 1.5rem;
  margin: 0.5rem 0;
}

.user-management {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}

.user-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
}

.user-table th, .user-table td {
  text-align: left;
  padding: 1rem;
  border-bottom: 1px solid #eee;
}

.role-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  text-transform: uppercase;
  font-weight: bold;
}
.role-badge.admin {
  background: #fef3c7;
  color: #d97706;
}
.role-badge.user {
  background: #e0e7ef;
  color: #475569;
}

.btn-delete {
  background: #fee2e2;
  color: #dc2626;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
  transition: background 0.2s;
}
.btn-delete:hover {
  background: #fca5a5;
}

.btn-view {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
  margin-right: 0.5rem;
  transition: background 0.2s;
}
.btn-view:hover {
  background: #2563eb;
}
</style>
